import React, { createContext, useContext, useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useAuth } from './AuthContext';
import { 
  getStripeAccountStatus, 
  createStripeConnectAccount, 
  createStripeAccountLink, 
  disconnectStripeAccount,
  createPaymentIntent,
  updateStripePaymentSettings
} from '../utils/stripeApi';
import { getCurrentUser } from '@aws-amplify/auth';

// Create context
const StripeContext = createContext(null);

// Get Stripe publishable key from environment variables
let stripePromise;
try {
  const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  if (!stripeKey) {
    console.warn('Stripe publishable key is missing or empty');
    stripePromise = null;
  } else {
    stripePromise = loadStripe(stripeKey).catch(err => {
      console.error('Failed to load Stripe:', err);
      return null;
    });
  }
} catch (error) {
  console.error('Error initializing Stripe:', error);
  stripePromise = null;
}

// Export a function to get the stripePromise for use in other components
export const getStripePromise = () => stripePromise;

export const StripeProvider = ({ children }) => {
  const user = useAuth(); // Get current user from auth context
  const [userId, setUserId] = useState(null);
  const [accountStatus, setAccountStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stripeLoaded, setStripeLoaded] = useState(Boolean(stripePromise));
  // New state to track if a connect flow is in progress
  const [connectFlowInProgress, setConnectFlowInProgress] = useState(false);

  // Set error if Stripe failed to load
  useEffect(() => {
    if (!stripePromise) {
      setError('Stripe failed to initialize. Payment features may not work correctly.');
    }
  }, []);

  // Get the actual user ID from Cognito when component mounts
  useEffect(() => {
    const fetchUserId = async () => {
      if (user && user.isAuthenticated) {
        try {
          const currentUser = await getCurrentUser();
          setUserId(currentUser.userId || currentUser.username);
        } catch (err) {
          console.error('Error fetching user ID:', err);
          setError('Failed to fetch user ID');
        }
      }
    };
    
    fetchUserId();
  }, [user]);

  // Check URL for return parameters from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true' || params.get('refresh') === 'true') {
      // Clear the URL params
      const newUrl = window.location.pathname;
      window.history.pushState({ path: newUrl }, '', newUrl);
      
      // If returning from Stripe, refresh the account status and end connect flow
      if (user && user.isAuthenticated && userId) {
        refreshAccountStatus();
        setConnectFlowInProgress(false);
      }
    }
  }, [user, userId]);

  // Function to refresh account status
  const refreshAccountStatus = async () => {
    if (!user || !user.isAuthenticated || !userId) {
      setAccountStatus(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const status = await getStripeAccountStatus(userId);
      
      // Handle null response (no account found - normal scenario)
      if (status === null) {
        setAccountStatus(null);
        setError(null);
      } else {
        setAccountStatus(status);
        setError(null);
        
        // Update connect flow status based on account status
        if (status && status.status === 'active') {
          setConnectFlowInProgress(false);
        }
      }
    } catch (err) {
      console.error('Error fetching Stripe account status:', err);
      setError(err.message || 'Failed to fetch account status');
      setAccountStatus(null);
    } finally {
      setLoading(false);
    }
  };

  // Check onboarding status explicitly
  const checkOnboardingStatus = async () => {
    if (!user || !user.isAuthenticated || !userId || !accountStatus || !accountStatus.accountId) {
      return;
    }

    try {
      setLoading(true);
      const status = await getStripeAccountStatus(userId);
      
      // Update our local state
      setAccountStatus(status);
      
      // If account is now active, end the connect flow
      if (status && status.status === 'active') {
        setConnectFlowInProgress(false);
      }
      
      return status;
    } catch (err) {
      console.error('Error checking onboarding status:', err);
      setError(err.message || 'Failed to check onboarding status');
    } finally {
      setLoading(false);
    }
  };

  // Connect a new Stripe account
  const connectAccount = async (email, businessName) => {
    console.log("Connect account called with:", { email, businessName });
    console.log("Current user state:", user);
    console.log("Current userId:", userId);
    
    if (!user || !user.isAuthenticated) {
      throw new Error('You must be logged in to connect a Stripe account');
    }
    
    if (!userId) {
      throw new Error('User ID is not available yet. Please try again in a moment.');
    }

    try {
      setLoading(true);
      setConnectFlowInProgress(true);
      setError(null);
      
      const result = await createStripeConnectAccount({
        userId: userId,
        email: email || user.email,
        businessName: businessName || ''
      });
      
      // Redirect to Stripe onboarding
      if (result && result.accountLink) {
        window.location.href = result.accountLink;
      } else {
        // If no account link but we have an account ID, we may need to refresh
        if (result && result.accountId) {
          await refreshAccountStatus();
        } else {
          throw new Error('Failed to get account link from Stripe');
        }
      }
      
      return result;
    } catch (err) {
      console.error('Error connecting Stripe account:', err);
      setConnectFlowInProgress(false);
      
      // Check if this is a backend API key error
      if (err.message && err.message.includes('Invalid API Key provided')) {
        setError('The Stripe integration is currently unavailable. Please contact support.');
      } else {
        setError(err.message || 'Failed to connect Stripe account');
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Create a new account link for onboarding
  const createAccountLink = async () => {
    if (!user || !user.isAuthenticated || !userId) {
      throw new Error('User must be authenticated');
    }

    try {
      setLoading(true);
      setConnectFlowInProgress(true);
      const result = await createStripeAccountLink(userId);
      
      // Redirect to Stripe onboarding
      if (result && result.accountLink) {
        window.location.href = result.accountLink;
      } else {
        throw new Error('Failed to get account link from Stripe');
      }
      
      return result;
    } catch (err) {
      console.error('Error creating account link:', err);
      setConnectFlowInProgress(false);
      setError(err.message || 'Failed to create account link');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Disconnect Stripe account
  const disconnectAccount = async () => {
    if (!user || !user.isAuthenticated || !userId) {
      throw new Error('User must be authenticated');
    }

    try {
      setLoading(true);
      await disconnectStripeAccount(userId);
      
      // Reset state after disconnection
      setAccountStatus(null);
      setConnectFlowInProgress(false);
      setError(null);
      
      // Force refresh the account status to ensure UI is in sync
      await refreshAccountStatus();
      
      return true;
    } catch (err) {
      console.error('Error disconnecting Stripe account:', err);
      setError(err.message || 'Failed to disconnect Stripe account');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Create a payment intent
  const createPayment = async (paymentData) => {
    try {
      setLoading(true);
      const result = await createPaymentIntent(paymentData);
      return result;
    } catch (err) {
      console.error('Error creating payment intent:', err);
      setError(err.message || 'Failed to create payment intent');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update payment settings
  const updatePaymentSettings = async (settings) => {
    if (!user || !user.isAuthenticated || !userId) {
      throw new Error('User must be authenticated');
    }

    try {
      setLoading(true);
      
      const result = await updateStripePaymentSettings({
        userId,
        settings
      });
      
      // Update local account status with new settings
      if (accountStatus) {
        setAccountStatus({
          ...accountStatus,
          paymentSettings: {
            ...accountStatus.paymentSettings,
            ...settings
          }
        });
      }
      
      return result;
    } catch (err) {
      console.error('Error updating payment settings:', err);
      setError(err.message || 'Failed to update payment settings');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Fetch account status when userId changes
  useEffect(() => {
    if (user && user.isAuthenticated && userId) {
      // Don't let Stripe account status errors block the app
      refreshAccountStatus().catch(error => {
        // Silently handle expected Stripe Connect errors (most users won't have accounts)
        if (!error.message?.includes('400') && !error.message?.includes('404')) {
          console.warn('Stripe account status check failed, continuing without it:', error.message);
        }
      });
    }
  }, [user, userId]);

  // Context value
  const value = {
    accountStatus,
    loading,
    error,
    stripeLoaded,
    connectFlowInProgress,
    refreshAccountStatus,
    checkOnboardingStatus,
    connectAccount,
    createAccountLink,
    disconnectAccount,
    createPayment,
    updatePaymentSettings
  };

  // Provide Stripe context with Elements, conditionally
  return (
    <StripeContext.Provider value={value}>
      {stripePromise ? (
        <Elements stripe={stripePromise}>
          {children}
        </Elements>
      ) : (
        // Render children without Stripe Elements if Stripe failed to load
        children
      )}
    </StripeContext.Provider>
  );
};

// Custom hook to use the Stripe context
export const useStripe = () => {
  const context = useContext(StripeContext);
  if (!context) {
    throw new Error('useStripe must be used within a StripeProvider');
  }
  return context;
};

export default StripeContext;